/**
 * RecipeEditorPage — Iter276-recoat-ui (v2)
 *
 * Stand-alone page at `/recipe-editor/:rowId` for managing the
 * `material_recipes` entries bound to one catalogue row. Linked
 * from each row in the Catalogue Audit list.
 *
 * Iter276-recoat-ui-v2 additions:
 *  • `unit_size` column (1L / 5L / 10L / 15L / 20L combobox)
 *  • Combobox-style inputs (HTML datalist) for product_name,
 *    supplier, supplier_code, unit, unit_size — the list grows
 *    every time the operator saves a new value.
 *  • Picking a known `product_name` auto-fills supplier / code /
 *    unit / unit_size / materials_desc (only the blank fields, so
 *    operator edits aren't trampled).
 *  • "📋 Copy from…" button — clone the entries from another
 *    recipe (any catalogue row) as a starting point, then make
 *    the small adjustments needed for this paint/coating job.
 *
 * Every save goes through `PUT /api/material-recipes/{rowId}` which
 * is admin-gated server-side.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Trash2, Plus, Save, ArrowLeft, ChefHat, Loader2, Copy, X, Search, Undo2, Check, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const BLANK_ENTRY = {
  product_name: '', supplier: '', supplier_code: '',
  materials_desc: '', unit: '', unit_size: '', ratio: 0,
  coverage_rate: 0, materials_rate: 0, labour_rate: 0,
  round_up: true, is_stock: false, supply_only: false, coverage_notes: '',
};

// Ratio (material per m²) and coverage_rate (m² per material unit)
// are mathematical reciprocals. When a recipe is loaded (or a value is
// edited) and one is missing, auto-derive from the other so operators
// never see a stale "0" alongside a filled ratio.
function backfillReciprocals(entries) {
  return (entries || []).map((e) => {
    const r = Number(e?.ratio || 0);
    const c = Number(e?.coverage_rate || 0);
    if (r > 0 && !c) return { ...e, coverage_rate: Number((1 / r).toFixed(4)) };
    if (c > 0 && !r) return { ...e, ratio:         Number((1 / c).toFixed(4)) };
    return e;
  });
}

// Iter276-labour Phase 2e — labour task presets and blank template.
const BLANK_LABOUR_TASK = {
  key: 'misc', label: '', tier: 'general', hours_per_unit: 0, notes: '',
};
const LABOUR_TASK_CHIPS = [
  { key: 'prep',    label: 'Prep',     default_tier: 'general' },
  { key: 'paint',   label: 'Paint',    default_tier: 'skilled' },
  { key: 'pack_up', label: 'Pack-up',  default_tier: 'general' },
  { key: 'travel',  label: 'Travel',   default_tier: 'general' },
  { key: 'misc',    label: 'Misc',     default_tier: 'general' },
];

const RECIPE_REGION_KEY = 'recipeEditor.region';
const SUPPORTED_REGIONS = [
  { key: 'hamilton',       label: 'Hamilton' },
  { key: 'waikato_region', label: 'Waikato Region' },
  { key: 'cambridge',      label: 'Cambridge' },
  { key: 'auckland',       label: 'Auckland' },
  { key: 'tauranga',       label: 'Tauranga' },
  { key: 'taupo',          label: 'Taupō' },
  { key: 'wellington',     label: 'Wellington' },
  { key: 'christchurch',   label: 'Christchurch' },
  { key: 'other',          label: 'Other' },
];

const EMPTY_VOCAB = {
  products: [], suppliers: [], codes: [],
  units: [], unit_sizes: [], product_links: {},
};

export default function RecipeEditorPage({ rowId: rowIdProp, onClose, embedded = false } = {}) {
  const routeParams = useParams();
  const rowId = rowIdProp || routeParams.rowId;
  const navigate = useNavigate();
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [entries, setEntries] = useState([]);
  const [labourTasks, setLabourTasks] = useState([]);
  const [labourModel, setLabourModel] = useState(null);
  const [region, setRegionState] = useState(() => {
    try { return localStorage.getItem(RECIPE_REGION_KEY) || 'hamilton'; } catch { return 'hamilton'; }
  });
  const setRegion = (r) => {
    setRegionState(r);
    try { localStorage.setItem(RECIPE_REGION_KEY, r); } catch { /* ignore */ }
  };
  const [meta, setMeta] = useState({ name: '', component: '', notes: '' });
  const [catalogueRow, setCatalogueRow] = useState(null);
  const [vocab, setVocab] = useState(EMPTY_VOCAB);
  const [showCopyModal, setShowCopyModal] = useState(false);

  // ─── Auto-save + Undo state ──────────────────────────────────────
  const AUTOSAVE_KEY = 'recipeEditor.autoSave';
  const [autoSave, setAutoSaveState] = useState(() => {
    try { return localStorage.getItem(AUTOSAVE_KEY) !== '0'; } catch { return true; }
  });
  const setAutoSave = (v) => {
    setAutoSaveState(v);
    try { localStorage.setItem(AUTOSAVE_KEY, v ? '1' : '0'); } catch { /* ignore */ }
  };
  const [history, setHistory] = useState([]);  // stack of past snapshots
  const [dirty, setDirty] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const skipDirtyRef = useRef(false);  // suppress auto-save right after load/save/undo apply

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: recRes }, catSettled, vocabSettled] = await Promise.all([
        api.get(`/material-recipes/${rowId}?region=${region}`),
        api.get(`/component-rate-tables/${rowId}/info`).catch(() => ({ data: null })),
        api.get('/material-recipes-vocab').catch(() => ({ data: EMPTY_VOCAB })),
      ]);
      const catRes = catSettled?.data;
      const recipe = recRes?.recipe;
      setLabourModel(recRes?.labour_model || null);
      // Recipe title is ALWAYS the catalogue row name — the recipe
      // is bound 1:1 to that row, so we don't allow drift.
      const canonicalName = catRes?.row?.name || recipe?.name || '';
      const canonicalComponent = catRes?.row?.component || recipe?.component || '';
      if (recipe) {
        setEntries(backfillReciprocals(recipe.entries || []));
        setLabourTasks(recipe.labour_tasks || []);
        setMeta({
          name: canonicalName,
          component: canonicalComponent,
          notes: recipe.notes || '',
        });
      } else {
        setEntries([]);
        setLabourTasks([]);
        setMeta({
          name: canonicalName,
          component: canonicalComponent,
          notes: '',
        });
      }
      setCatalogueRow(catRes?.row || null);
      setVocab(vocabSettled?.data || EMPTY_VOCAB);
      // Fresh load is by definition not dirty; reset history too.
      skipDirtyRef.current = true;
      setHistory([]);
      setDirty(false);
    } catch (err) {
      toast.error('Failed to load recipe');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [rowId, api, region]);

  useEffect(() => { load(); }, [load]);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const payload = entries.map((e) => ({
        ...e,
        ratio: Number(e.ratio || 0),
        coverage_rate: Number(e.coverage_rate || 0),
        materials_rate: Number(e.materials_rate || 0),
        labour_rate: Number(e.labour_rate || 0),
        round_up: !!e.round_up,
        is_stock: !!e.is_stock,
        supply_only: !!e.supply_only,
      }));
      const labourPayload = (labourTasks || []).map((t) => ({
        key:            t.key || 'misc',
        label:          t.label || '',
        tier:           t.tier || 'general',
        hours_per_unit: Number(t.hours_per_unit || 0),
        notes:          t.notes || '',
      }));
      const { data } = await api.put(`/material-recipes/${rowId}?region=${region}`, {
        component: meta.component,
        name: meta.name,
        notes: meta.notes,
        entries: payload,
        labour_tasks: labourPayload,
      });
      if (data?.labour_model) setLabourModel(data.labour_model);
      setDirty(false);
      setLastSavedAt(new Date());
      // Refresh vocab so newly-saved products/suppliers/codes appear
      // in the combobox suggestions on the next edit.
      try {
        const { data: v } = await api.get('/material-recipes-vocab');
        setVocab(v || EMPTY_VOCAB);
      } catch { /* non-fatal */ }
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(`Save failed: ${detail}`);
    } finally {
      setSaving(false);
    }
  }, [entries, labourTasks, meta, rowId, api, region]);

  // Auto-save: 1.5s after the last edit, push the recipe.
  useEffect(() => {
    if (!autoSave || !dirty || loading || saving) return undefined;
    const t = setTimeout(() => { save(); }, 1500);
    return () => clearTimeout(t);
  }, [autoSave, dirty, loading, saving, entries, labourTasks, meta, save]);

  // Anything else that flips entries/meta/labour tasks after the
  // initial load marks the recipe dirty (drives auto-save + Save
  // button state).
  useEffect(() => {
    if (skipDirtyRef.current) {
      skipDirtyRef.current = false;
      return;
    }
    setDirty(true);
  }, [entries, labourTasks, meta]);

  // Snapshot helper for the undo stack — capped at 50 entries.
  const pushHistory = useCallback(() => {
    setHistory((prev) => {
      const snap = {
        entries: JSON.parse(JSON.stringify(entries)),
        labourTasks: JSON.parse(JSON.stringify(labourTasks)),
        meta: { ...meta },
      };
      return [...prev.slice(-49), snap];
    });
  }, [entries, labourTasks, meta]);

  const undo = useCallback(() => {
    setHistory((prev) => {
      if (prev.length === 0) {
        toast.info('Nothing to undo');
        return prev;
      }
      const last = prev[prev.length - 1];
      // Restoring counts as an edit — auto-save will persist it.
      setEntries(last.entries);
      setLabourTasks(last.labourTasks || []);
      setMeta(last.meta);
      toast.success('Undone');
      return prev.slice(0, -1);
    });
  }, []);

  // Ctrl/Cmd+Z keyboard shortcut for undo.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo]);

  // ─────────────────────────────────────────────────────────────
  // Picking a known product_name auto-fills the supplier, code,
  // unit, unit_size and materials_desc (only when those fields
  // are still blank on the entry — operator edits win).
  // ─────────────────────────────────────────────────────────────
  const applyProductLink = useCallback((entry, productName) => {
    const link = vocab.product_links?.[productName];
    if (!link) return { ...entry, product_name: productName };
    return {
      ...entry,
      product_name:   productName,
      supplier:       entry.supplier       || link.supplier       || '',
      supplier_code:  entry.supplier_code  || link.supplier_code  || '',
      unit:           entry.unit           || link.unit           || '',
      unit_size:      entry.unit_size      || link.unit_size      || '',
      materials_desc: entry.materials_desc || link.materials_desc || '',
    };
  }, [vocab]);

  const update = (i, key, value) => {
    pushHistory();
    setEntries((prev) => prev.map((e, idx) => {
      if (idx !== i) return e;
      if (key === 'product_name') return applyProductLink(e, value);
      const next = { ...e, [key]: value };
      // Auto-derive the sister field when one of the reciprocal pair
      // (ratio ↔ coverage_rate) is edited. ratio = material units per m²
      // (e.g. 7 screws/m² or 0.1 L/m²); coverage_rate = m² per material
      // unit (reciprocal).
      // HOTFIX 2026-09-14 (Audit C P0): previously only auto-filled the
      // sister when the OTHER was empty. Editing an existing coverage_rate
      // left `ratio` stale → cost display updated on screen but ORDER qty
      // used the stale ratio. Silent divergence.
      // Now: ALWAYS recompute the sister on any edit of either field.
      if (key === 'ratio') {
        const r = Number(value);
        if (r > 0) {
          next.coverage_rate = Number((1 / r).toFixed(4));
        }
      } else if (key === 'coverage_rate') {
        const c = Number(value);
        if (c > 0) {
          next.ratio = Number((1 / c).toFixed(4));
        }
      }
      return next;
    }));
  };
  const remove = (i) => {
    pushHistory();
    setEntries((prev) => prev.filter((_, idx) => idx !== i));
  };
  const addRow = () => {
    pushHistory();
    setEntries((prev) => [...prev, { ...BLANK_ENTRY }]);
  };
  const updateMeta = (patch) => {
    pushHistory();
    setMeta((m) => ({ ...m, ...patch }));
  };

  // Iter276-labour Phase 2e — labour task helpers.
  const addLabourTask = (chip) => {
    pushHistory();
    setLabourTasks((prev) => [
      ...prev,
      { ...BLANK_LABOUR_TASK, key: chip.key, label: chip.label, tier: chip.default_tier },
    ]);
  };
  const updateLabourTask = (i, key, value) => {
    pushHistory();
    setLabourTasks((prev) => prev.map((t, idx) => (idx === i ? { ...t, [key]: value } : t)));
  };
  const removeLabourTask = (i) => {
    pushHistory();
    setLabourTasks((prev) => prev.filter((_, idx) => idx !== i));
  };

  // Merge persisted vocab with the values typed into THIS recipe so
  // suggestions surface immediately across sibling entries, even
  // before the operator saves. Cross-recipe propagation still
  // requires Save (the server rebuilds the vocab on every PUT).
  const mergedVocab = useMemo(() => {
    const merge = (base, picker) => {
      const seen = new Set();
      const out = [];
      const add = (raw) => {
        const v = (raw || '').toString().trim();
        if (!v) return;
        const k = v.toLowerCase();
        if (seen.has(k)) return;
        seen.add(k);
        out.push(v);
      };
      (base || []).forEach(add);
      (entries || []).forEach((e) => add(picker(e)));
      return out.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
    };
    return {
      products:   merge(vocab.products,   (e) => e.product_name),
      suppliers:  merge(vocab.suppliers,  (e) => e.supplier),
      codes:      merge(vocab.codes,      (e) => e.supplier_code),
      units:      merge(vocab.units,      (e) => e.unit),
      unit_sizes: merge(vocab.unit_sizes, (e) => e.unit_size),
    };
  }, [vocab, entries]);

  // Iter276e — Live client-side cost rollup. Mirrors backend logic in
  // `compute_recipe_costs` so the summary updates instantly as the
  // operator types — no debounce or fetch needed.
  const costRollup = useMemo(() => {
    const parseSize = (raw) => {
      if (raw == null) return 0;
      const m = String(raw).match(/[\d.]+/);
      return m ? Number(m[0]) || 0 : 0;
    };
    let ordered = 0, allIn = 0, labour = 0, incomplete = 0, totalEntries = 0;
    (entries || []).forEach((e) => {
      const name = (e.product_name || '').trim();
      if (!name) return;
      totalEntries += 1;
      const packSize = parseSize(e.unit_size);
      const cov = Number(e.coverage_rate || 0);
      const price = Number(e.materials_rate || 0);
      const labRate = Number(e.labour_rate || 0);
      const supplyOnly = !!e.supply_only;
      let hasInputs, buy;
      if (supplyOnly) {
        // Count-based item — needs price + pack size, no coverage.
        hasInputs = packSize > 0 && price > 0;
        buy = 0;  // per-m² rollup unaffected
      } else {
        // Iter276-audit — pack coverage is now the total coverage of ONE
        // pack, i.e. coverage_rate is "quote units per pack" (not per
        // material sub-unit). unit_size is descriptive only.
        const packCoverage = cov;
        hasInputs = packCoverage > 0 && price > 0;
        buy = hasInputs ? price / packCoverage : 0;
      }
      if (!hasInputs) incomplete += 1;
      if (!supplyOnly) {
        allIn += buy;
        if (!e.is_stock) ordered += buy;
      }
      labour += labRate;
    });
    // Iter276-labour Phase 2e — labour tasks cost, resolved via the
    // active labour model's tier charge_hourly.
    const tierLookup = new Map(
      ((labourModel?.staff_tiers) || []).map((t) => [t.key, Number(t.charge_hourly) || 0])
    );
    let labourTasksCost = 0;
    const labourTasksBreakdown = (labourTasks || []).map((t) => {
      const hrs = Number(t.hours_per_unit || 0);
      const rate = tierLookup.get(t.tier) || 0;
      const c = hrs * rate;
      labourTasksCost += c;
      return { ...t, charge_hourly: rate, cost_per_unit: c };
    });
    const labourTotal = labour + labourTasksCost;
    return {
      materialsOrdered: ordered,
      materialsAllIn:   allIn,
      labour,                // legacy per-entry labour_rate sum
      labourTasksCost,       // Phase 2e — from labour_tasks[]
      labourTotal,           // labour + labourTasksCost
      labourTasksBreakdown,
      totalOrdered:     ordered + labourTotal,
      totalAllIn:       allIn + labourTotal,
      incomplete,
      totalEntries,
    };
  }, [entries, labourTasks, labourModel]);

  const applyClone = (clonedEntries) => {
    pushHistory();
    setEntries(clonedEntries.map((e) => ({ ...BLANK_ENTRY, ...e })));
    setShowCopyModal(false);
    toast.success(`Copied ${clonedEntries.length} entr${clonedEntries.length === 1 ? 'y' : 'ies'} — edit and save when ready`);
  };

  // Iter276e (Phase 1) — Recipe Editor labels adapt to the catalogue
  // row's unit. `/each` rows compute cost per unit (e.g. per door);
  // `/m²` rows compute cost per m². The math is identical — only the
  // labels change so operators aren't confused when a per-door recipe
  // shows "m² per L" (nonsense for a garage door).
  //
  // When the parent row's `unit` is blank (common for ridge/spouting
  // rows where the operator never explicitly set it), fall back to
  // the component's typical unit so labels display as /lm not /m².
  const COMPONENT_DEFAULT_UNIT = {
    ridge: '/lm', barge: '/lm', valley: '/lm', spouting: '/lm', flashing: '/lm',
    sheet: '/m²', tile: '/m²', underlay: '/m²', recoat: '/m²',
    aquaseal: '/each', fastening: '/m²', cleaning: '/m²',
  };
  const rawUnit = (catalogueRow?.unit || '').trim();
  const catUnit = rawUnit || COMPONENT_DEFAULT_UNIT[(catalogueRow?.component || '').toLowerCase()] || '/m²';
  const unitLabels = useMemo(() => {
    const u = catUnit.toLowerCase();
    if (u === '/each' || u === 'each') {
      return {
        perUnit:        '/each',
        packCoverage:   'each per pack',
        coverageColumn: 'Pack coverage (each/pack)',
        rollupSuffix:   '/each',
      };
    }
    if (u === '/mtr' || u === 'mtr' || u === '/lm' || u === 'lm') {
      return {
        perUnit:        '/lm',
        packCoverage:   'lm per pack',
        coverageColumn: 'Pack coverage (lm/pack)',
        rollupSuffix:   '/lm',
      };
    }
    // Default: per square metre
    return {
      perUnit:        '/m²',
      packCoverage:   'm² per pack',
      coverageColumn: 'Pack coverage (m²/pack)',
      rollupSuffix:   '/m²',
    };
  }, [catUnit]);

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="recipe-editor-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="min-w-0 flex-1">
          <button
            onClick={() => {
              // Embedded (modal) mode → parent supplies onClose.
              if (embedded && onClose) { onClose(); return; }
              // Recipe editor is often opened in a new tab from the
              // Materials Audit Console — in that case `navigate(-1)`
              // does nothing (no history). Fall back to the audit
              // console so Back is always useful.
              if (window.history.state?.idx && window.history.state.idx > 0) {
                navigate(-1);
              } else {
                navigate('/admin/materials-audit');
              }
            }}
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 mb-1"
            data-testid="recipe-editor-back"
          >
            <ArrowLeft size={12} /> Back
          </button>
          <div className="text-[10px] uppercase tracking-wider text-violet-600 font-bold flex items-center gap-1.5 mb-1">
            <ChefHat size={12} /> Recipe Editor
          </div>
          <h1
            data-testid="recipe-editor-title"
            className="text-2xl font-bold text-slate-900 truncate"
            title="Recipe title — derived from the catalogue row name"
          >
            {meta.name || <span className="text-slate-400 italic font-normal">Loading…</span>}
          </h1>
          <p className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5">
            Recoat work type · {meta.component || '—'}
          </p>
          {catalogueRow && (
            <p className="text-[10px] text-slate-400 mt-0.5">
              Catalogue row · <code>{rowId}</code>
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <SaveStatus saving={saving} dirty={dirty} lastSavedAt={lastSavedAt} autoSave={autoSave} />
          <label className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500 px-2 py-1.5 rounded-md border border-slate-300 bg-white cursor-pointer select-none" title="Which region's labour cost model to use for labour task pricing">
            <span className="text-slate-400 font-bold">Region</span>
            <select
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              data-testid="recipe-editor-region-select"
              className="text-xs font-normal normal-case bg-transparent focus:outline-none"
            >
              {SUPPORTED_REGIONS.map((r) => (
                <option key={r.key} value={r.key}>{r.label}</option>
              ))}
            </select>
          </label>
          <button
            onClick={undo}
            disabled={history.length === 0}
            data-testid="recipe-editor-undo-btn"
            title="Undo last change (Ctrl/Cmd+Z)"
            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Undo2 size={12} /> Undo
            {history.length > 0 && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-slate-200 text-slate-700 text-[9px] font-bold">{history.length}</span>
            )}
          </button>
          <label
            className="inline-flex items-center gap-1.5 text-xs px-2 py-1.5 rounded-md border border-slate-300 bg-white cursor-pointer select-none"
            title="Auto-save 1.5s after each edit"
          >
            <input
              type="checkbox"
              checked={autoSave}
              onChange={(e) => setAutoSave(e.target.checked)}
              data-testid="recipe-editor-autosave-toggle"
              className="accent-violet-600"
            />
            Auto-save
          </label>
          <button
            onClick={() => setShowCopyModal(true)}
            data-testid="recipe-editor-copy-from-btn"
            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100"
            title="Clone the entries from another recipe as a starting point"
          >
            <Copy size={12} /> Copy from…
          </button>
          <button
            onClick={addRow}
            data-testid="recipe-editor-add-row-btn"
            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50"
          >
            <Plus size={12} /> Add product
          </button>
          <button
            onClick={save}
            disabled={saving || !dirty}
            data-testid="recipe-editor-save-btn"
            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            {saving ? 'Saving…' : 'Save now'}
          </button>
        </div>
      </div>

      {catalogueRow && (
        <div className="mb-4 border border-slate-200 rounded-md bg-slate-50/50 p-3 text-xs text-slate-600 grid grid-cols-2 md:grid-cols-4 gap-2">
          <div><b>Supplier (legacy lump):</b> {catalogueRow.supplier || '—'}</div>
          <div><b>Materials desc:</b> {catalogueRow.materials_desc || '—'}</div>
          <div><b>Rate ($/unit):</b> {catalogueRow.materials_rate ?? '—'}</div>
          <div><b>Unit:</b> {catalogueRow.unit || '—'}</div>
        </div>
      )}

      {/* Iter276e — Recipe cost rollup strip. Updates live as the
          operator edits any entry. Two materials columns:
          • ORDERED  — excludes is_stock items (what you buy per job)
          • ALL-IN   — includes is_stock items (true bill of materials,
                       auto-synced to catalogue row's buy_cost on save) */}
      <CostRollupStrip rollup={costRollup} catalogueBuyCost={catalogueRow?.buy_cost} unitSuffix={unitLabels.rollupSuffix} testid="recipe-cost-rollup" />

      {/* Shared <datalist>s — referenced by id from every InputCell
          on the page. Browser renders these as native combobox
          suggestions while still letting the operator type new
          values. We merge:
            (a) the persisted vocab from `/material-recipes-vocab`
            (b) the values typed into THIS recipe's entries right
                now (so suggestions are live across the same recipe
                before Save).
          Cross-recipe propagation still requires Save (vocab is
          rebuilt server-side on every save). */}
      <datalist id="vocab-products">
        {mergedVocab.products.map((p) => <option key={p} value={p} />)}
      </datalist>
      <datalist id="vocab-suppliers">
        {mergedVocab.suppliers.map((p) => <option key={p} value={p} />)}
      </datalist>
      <datalist id="vocab-codes">
        {mergedVocab.codes.map((p) => <option key={p} value={p} />)}
      </datalist>
      <datalist id="vocab-units">
        {mergedVocab.units.map((p) => <option key={p} value={p} />)}
      </datalist>
      <datalist id="vocab-unit-sizes">
        {mergedVocab.unit_sizes.map((p) => <option key={p} value={p} />)}
      </datalist>

      {loading ? (
        <div className="py-16 text-center text-slate-400">Loading recipe…</div>
      ) : entries.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-md p-12 text-center text-slate-500" data-testid="recipe-editor-empty">
          No recipe entries yet. Click <b>Add product</b> to start composing the materials breakdown,
          or <b>Copy from…</b> to clone another recipe and tweak.
        </div>
      ) : (
        <div className="space-y-2">
          {entries.map((e, i) => (
            <div
              key={i}
              data-testid={`recipe-row-${i}`}
              className="border border-slate-200 rounded-md bg-white p-3 shadow-sm"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-violet-100 text-violet-700 text-[10px] font-bold">
                  #{i + 1}
                </span>
                <button
                  onClick={() => remove(i)}
                  className="text-rose-500 hover:text-rose-700"
                  data-testid={`recipe-row-${i}-delete`}
                  title="Remove this product"
                >
                  <Trash2 size={14} />
                </button>
              </div>

              {/* Row 1 — identity: product / supplier / code / unit / unit size */}
              <div className="grid grid-cols-12 gap-2 mb-2">
                <Cell label="Product name" span={4}>
                  <InputCell value={e.product_name} onChange={(v) => update(i, 'product_name', v)} placeholder="CCM Galv Primer" list="vocab-products" testid={`recipe-row-${i}-product`} />
                </Cell>
                <Cell label="Supplier" span={3}>
                  <InputCell value={e.supplier} onChange={(v) => update(i, 'supplier', v)} placeholder="CCM" list="vocab-suppliers" testid={`recipe-row-${i}-supplier`} />
                </Cell>
                <Cell label="Code" span={2}>
                  <InputCell value={e.supplier_code} onChange={(v) => update(i, 'supplier_code', v)} placeholder="GPS04" list="vocab-codes" mono testid={`recipe-row-${i}-code`} />
                </Cell>
                <Cell label="Unit" span={2}>
                  <InputCell value={e.unit} onChange={(v) => update(i, 'unit', v)} placeholder="bucket" list="vocab-units" testid={`recipe-row-${i}-unit`} />
                </Cell>
                <Cell label="Unit size" span={1} title="Descriptor only (e.g. 20 kg, 10L) — no longer part of the coverage math.">
                  <InputCell value={e.unit_size} onChange={(v) => update(i, 'unit_size', v)} placeholder="10L" list="vocab-unit-sizes" testid={`recipe-row-${i}-unit-size`} />
                </Cell>
              </div>

              {/* Row 2 — quantitative + notes */}
              <div className="grid grid-cols-12 gap-2">
                <Cell label="Description" span={2}>
                  <InputCell value={e.materials_desc} onChange={(v) => update(i, 'materials_desc', v)} placeholder="Galv Primer · 4 L" testid={`recipe-row-${i}-desc`} />
                </Cell>
                <Cell label="Ratio" span={1} title="Packs per quote unit (e.g. bags per m²). Reciprocal of Pack coverage.">
                  <InputCell value={e.ratio} onChange={(v) => update(i, 'ratio', v)} placeholder="0.333" right number step="0.001" testid={`recipe-row-${i}-ratio`} />
                </Cell>
                <Cell label={unitLabels.coverageColumn} span={2} title={`Total quote units covered by ONE pack (${unitLabels.packCoverage}).`}>
                  <InputCell value={e.coverage_rate} onChange={(v) => update(i, 'coverage_rate', v)} placeholder="10" right number step="0.01" testid={`recipe-row-${i}-coverage-rate`} />
                </Cell>
                <Cell label="Product price $" span={2} title="Purchase price per pack/unit from the supplier">
                  <InputCell value={e.materials_rate} onChange={(v) => update(i, 'materials_rate', v)} placeholder="85" right number step="0.01" testid={`recipe-row-${i}-rate`} />
                </Cell>
                <Cell label="Labour rate $" span={2} title="Labour cost per quote unit to apply this product">
                  <InputCell value={e.labour_rate} onChange={(v) => update(i, 'labour_rate', v)} placeholder="0" right number step="0.01" testid={`recipe-row-${i}-labour-rate`} />
                </Cell>
                <Cell label="Round ↑ · Stock · Supply" span={1} title="Round-up to whole pack when ordering; Stock = kept on truck; Supply-only = count-based item with no per-m² coverage (e.g. rivets, cable ties)" center>
                  <div className="flex flex-col items-center justify-center gap-0.5">
                    <label className="flex items-center gap-1 cursor-pointer" title="Round-up to whole pack/bag when ordering">
                      <input
                        type="checkbox"
                        checked={!!e.round_up}
                        onChange={(ev) => update(i, 'round_up', ev.target.checked)}
                        data-testid={`recipe-row-${i}-round-up`}
                      />
                      <span className="text-[9px] text-slate-500">↑</span>
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer" title="Stock item — kept on truck, not ordered per-job">
                      <input
                        type="checkbox"
                        checked={!!e.is_stock}
                        onChange={(ev) => update(i, 'is_stock', ev.target.checked)}
                        data-testid={`recipe-row-${i}-is-stock`}
                      />
                      <span className="text-[9px] text-slate-500">📦</span>
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer" title="Supply-only — no coverage/m² math; audit won't flag missing coverage">
                      <input
                        type="checkbox"
                        checked={!!e.supply_only}
                        onChange={(ev) => update(i, 'supply_only', ev.target.checked)}
                        data-testid={`recipe-row-${i}-supply-only`}
                      />
                      <span className="text-[9px] text-slate-500">📦₁</span>
                    </label>
                  </div>
                </Cell>
                <Cell label="Coverage notes" span={2}>
                  <InputCell value={e.coverage_notes} onChange={(v) => update(i, 'coverage_notes', v)} placeholder="1 coat · prep prior" testid={`recipe-row-${i}-coverage`} />
                </Cell>
              </div>

              <CoverageMathStrip
                entry={e}
                unitLabels={unitLabels}
                onEditPackCoverage={(val) => {
                  // pack_coverage IS coverage_rate now — write straight through.
                  update(i, 'coverage_rate', Number(val) || 0);
                }}
                testid={`recipe-row-${i}-calc`}
              />
            </div>
          ))}
        </div>
      )}

      {/* Iter276-labour Phase 2e — Labour tasks section, driven by the
          active labour cost model for the selected region. */}
      {!loading && (
        <LabourTasksSection
          tasks={labourTasks}
          labourModel={labourModel}
          region={region}
          unitLabels={unitLabels}
          onAdd={addLabourTask}
          onUpdate={updateLabourTask}
          onRemove={removeLabourTask}
        />
      )}

      <div className="mt-4">
        <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Recipe notes</label>
        <textarea
          value={meta.notes}
          onChange={(e) => updateMeta({ notes: e.target.value })}
          placeholder="Free-form notes — application order, environmental constraints, alternatives…"
          rows={2}
          data-testid="recipe-editor-notes"
          className="w-full border border-slate-300 rounded-md px-2 py-1 text-xs"
        />
      </div>

      <div className="mt-6 text-[11px] text-slate-500">
        <Link to="/component-rates" className="hover:underline">← Component Rate Tables</Link>
      </div>

      {showCopyModal && (
        <CopyFromModal
          api={api}
          currentRowId={rowId}
          onClose={() => setShowCopyModal(false)}
          onApply={applyClone}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// SaveStatus — small badge that surfaces auto-save / dirty state.
// ─────────────────────────────────────────────────────────────────
function SaveStatus({ saving, dirty, lastSavedAt, autoSave }) {
  const [tick, setTick] = useState(0);
  // Re-render every 30s so "Saved 2m ago" stays fresh.
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);
  void tick; // tick is intentionally read only to trigger re-render

  let label, tone, Icon = Check;
  if (saving) {
    label = 'Saving…';
    tone = 'bg-violet-50 text-violet-700 ring-violet-200';
    Icon = Loader2;
  } else if (dirty) {
    label = autoSave ? 'Unsaved · auto-saving' : 'Unsaved changes';
    tone = 'bg-amber-50 text-amber-700 ring-amber-200';
    Icon = autoSave ? Loader2 : Save;
  } else if (lastSavedAt) {
    label = `Saved ${formatAgo(lastSavedAt)}`;
    tone = 'bg-emerald-50 text-emerald-700 ring-emerald-200';
  } else {
    label = 'No unsaved changes';
    tone = 'bg-slate-50 text-slate-500 ring-slate-200';
  }
  return (
    <span
      data-testid="recipe-editor-save-status"
      className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ring-1 ${tone}`}
      title={lastSavedAt ? `Last saved at ${lastSavedAt.toLocaleString()}` : undefined}
    >
      <Icon size={10} className={saving || (dirty && autoSave) ? 'animate-spin' : ''} />
      {label}
    </span>
  );
}

function formatAgo(date) {
  const s = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (s < 5) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ago`;
}


function InputCell({ value, onChange, placeholder, mono, right, number, step, list, testid }) {
  return (
    <input
      type={number ? 'number' : 'text'}
      step={step}
      list={list}
      value={value ?? ''}
      onChange={(e) => onChange(number ? Number(e.target.value) : e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className={`w-full border border-slate-200 rounded px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-violet-400 ${mono ? 'font-mono' : ''} ${right ? 'text-right tabular-nums' : ''}`}
    />
  );
}

// ─────────────────────────────────────────────────────────────────
// CoverageMathStrip — display-only calculated metrics per entry.
//   pack_size      = numeric portion of `unit_size` ("10L" → 10)
//   pack_coverage  = coverage_rate                (quote units per pack)
//   coverage_cost  = product_price ÷ pack_coverage ($ per m²)
// Greys out gracefully when any input is missing.
// ─────────────────────────────────────────────────────────────────
function parseUnitSize(raw) {
  if (raw === null || raw === undefined) return 0;
  const m = String(raw).match(/[\d.]+/);
  return m ? Number(m[0]) || 0 : 0;
}

// ─────────────────────────────────────────────────────────────────
// CostRollupStrip — top-of-page summary showing the recipe's combined
// materials buy cost, labour cost and total per m². Two materials
// columns:
//   • ORDERED  — excludes is_stock entries (what's bought per job)
//   • ALL-IN   — includes everything (the figure auto-synced to the
//                catalogue row's buy_cost on save)
// Highlights an "incomplete" warning pill when any entry is missing
// the inputs needed to compute its per-m² cost.
// ─────────────────────────────────────────────────────────────────
function CostRollupStrip({ rollup, catalogueBuyCost, unitSuffix = '/m²', testid }) {
  const fmt = (n) => `$${(n || 0).toFixed(2)}`;
  const fmt4 = (n) => `$${(n || 0).toFixed(4)}`;
  const hasAny = rollup.totalEntries > 0;
  const ordVsAll = rollup.materialsAllIn - rollup.materialsOrdered;
  const cat = Number(catalogueBuyCost || 0);
  const driftPct = cat > 0 && rollup.materialsAllIn > 0
    ? Math.abs((rollup.materialsAllIn - cat) / cat) * 100
    : 0;
  return (
    <div
      data-testid={testid}
      className="mb-4 border border-violet-200 rounded-lg bg-gradient-to-br from-violet-50 to-white p-3"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[10px] uppercase tracking-wider text-violet-700 font-bold flex items-center gap-2">
          Recipe rollup
          {rollup.incomplete > 0 && (
            <span
              data-testid={`${testid}-incomplete-pill`}
              className="inline-flex items-center gap-1 text-[10px] normal-case font-semibold tracking-normal px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 ring-1 ring-amber-200"
              title="Some entries are missing product price, coverage rate or unit size — rollup may underrepresent the true cost"
            >
              ⚠ {rollup.incomplete} of {rollup.totalEntries} entr{rollup.totalEntries === 1 ? 'y' : 'ies'} missing inputs
            </span>
          )}
        </div>
        {cat > 0 && rollup.materialsAllIn > 0 && (
          <div
            className={`text-[10px] font-mono px-2 py-0.5 rounded ${driftPct > 10 ? 'bg-rose-50 text-rose-700 ring-1 ring-rose-200' : 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'}`}
            title={`Recipe all-in vs catalogue buy_cost ($${cat.toFixed(2)})`}
          >
            vs catalogue {fmt(cat)} · {driftPct > 10 ? '⚠' : '✓'} {driftPct.toFixed(1)}% drift
          </div>
        )}
      </div>

      {!hasAny ? (
        <div className="mt-2 text-xs text-slate-500 italic">
          Add at least one product entry to see the cost rollup.
        </div>
      ) : (
        <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3">
          <RollupCell
            label="Materials · ordered"
            sub="excl. 📦 stock items"
            value={fmt4(rollup.materialsOrdered)}
            unit={unitSuffix}
            tone="violet"
            testid={`${testid}-ordered`}
          />
          <RollupCell
            label="Materials · all-in"
            sub="bill of materials"
            value={fmt4(rollup.materialsAllIn)}
            unit={unitSuffix}
            tone="emerald"
            testid={`${testid}-all-in`}
            badge={ordVsAll > 0.0001 ? `+${fmt(ordVsAll)} stock` : null}
          />
          <RollupCell
            label="Labour · total"
            sub={rollup.labourTasksCost > 0 ? `tasks $${(rollup.labourTasksCost || 0).toFixed(2)} + inline $${(rollup.labour || 0).toFixed(2)}` : 'sum of labour tasks'}
            value={fmt(rollup.labourTotal || rollup.labour)}
            unit={unitSuffix}
            tone="orange"
            testid={`${testid}-labour`}
          />
          <RollupCell
            label={`Total · all-in`}
            sub="materials + labour"
            value={fmt(rollup.totalAllIn)}
            unit={unitSuffix}
            tone="slate"
            testid={`${testid}-total`}
          />
        </div>
      )}
    </div>
  );
}

function RollupCell({ label, sub, value, unit, tone = 'slate', badge, testid }) {
  const TONE = {
    violet:  'text-violet-700  border-violet-200',
    emerald: 'text-emerald-700 border-emerald-200',
    orange:  'text-orange-700  border-orange-200',
    slate:   'text-slate-800   border-slate-300',
  };
  return (
    <div data-testid={testid} className={`bg-white border ${TONE[tone].split(' ')[1]} rounded-md px-2 py-1.5`}>
      <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold flex items-center justify-between gap-1">
        <span>{label}</span>
        {badge && <span className="font-mono normal-case tracking-normal text-[9px] text-slate-500">{badge}</span>}
      </div>
      <div className={`text-base font-bold tabular-nums ${TONE[tone].split(' ')[0]}`}>
        {value}
        <span className="text-[10px] font-normal text-slate-500 ml-0.5">{unit}</span>
      </div>
      <div className="text-[9px] text-slate-400 -mt-0.5">{sub}</div>
    </div>
  );
}


function CoverageMathStrip({ entry, unitLabels, onEditPackCoverage, testid }) {
  const packSize = parseUnitSize(entry?.unit_size);
  const cov = Number(entry?.coverage_rate || 0);
  const price = Number(entry?.materials_rate || 0);
  const labour = Number(entry?.labour_rate || 0);
  const supplyOnly = !!entry?.supply_only;
  // Iter276-audit — pack_coverage is coverage_rate directly (quote units
  // per PACK). unit_size stays as a descriptor (e.g. "20 kg" or "10L")
  // and is no longer part of the math.
  const packCoverage = cov;
  const coverageCost = packCoverage > 0 && price > 0 ? price / packCoverage : 0;
  const perUnitCost = packSize > 0 && price > 0 ? price / packSize : 0;
  const totalPerUnit = coverageCost + labour;
  const fmt = (n) => (n > 0 ? `$${n.toFixed(n < 0.1 ? 4 : 3)}` : '—');
  const uSuffix = unitLabels?.rollupSuffix || '/m²';
  const uPacked = unitLabels?.perUnit?.replace('/', '') || 'm²';
  const packDescriptor = (entry?.unit_size && entry?.unit)
    ? `${entry.unit_size} ${entry.unit}`
    : (entry?.unit || '');

  // Supply-only strip: show only the per-unit cost — no per-m² math.
  if (supplyOnly) {
    return (
      <div
        data-testid={testid}
        className="mt-2 pt-2 border-t border-slate-100 text-[11px] text-slate-500 flex flex-wrap gap-x-4 gap-y-1"
      >
        <span className="inline-flex items-center gap-1">
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200 uppercase tracking-wider">Supply only</span>
          <span>count-based · no per-m² coverage</span>
        </span>
        <span title="Product price ÷ pack size">
          Per-unit cost{' '}
          <b className="text-emerald-700 tabular-nums" data-testid={`${testid}-per-unit-cost`}>
            {fmt(perUnitCost)}
          </b>
        </span>
        {labour > 0 && (
          <span>
            Labour {uSuffix}{' '}
            <b className="text-orange-700 tabular-nums">${labour.toFixed(2)}</b>
          </span>
        )}
      </div>
    );
  }

  return (
    <div
      data-testid={testid}
      className="mt-2 pt-2 border-t border-slate-100 text-[11px] text-slate-500 flex flex-wrap gap-x-4 gap-y-1 items-center"
    >
      {/* Iter276-audit — pack_coverage IS coverage_rate now. The
          "quote units per pack" value drives all downstream math.
          unit_size is descriptive-only (e.g. "20 kg", "10L"). */}
      <span className="inline-flex items-center gap-1">
        <span>1 pack{packDescriptor ? ` (${packDescriptor})` : ''} covers</span>
        <input
          type="number"
          step="0.01"
          value={packCoverage > 0 ? Number(packCoverage.toFixed(4)) : ''}
          onChange={(e) => onEditPackCoverage && onEditPackCoverage(Number(e.target.value))}
          disabled={!onEditPackCoverage}
          title={`Total ${uPacked} covered by one pack. Also visible above as "${unitLabels?.coverageColumn}".`}
          className={`w-20 text-[11px] border rounded px-1.5 py-0.5 text-right tabular-nums focus:outline-none focus:ring-1 focus:ring-slate-400 font-semibold text-slate-800 ${(!onEditPackCoverage) ? 'bg-slate-50 border-slate-200 cursor-not-allowed' : 'border-slate-300 bg-white'}`}
          data-testid={`${testid}-pack-coverage`}
        />
        <span className="text-slate-500">{uPacked}</span>
      </span>
      <span className="text-slate-400" title={`Per-pack coverage. Above column shows same value.`}>
        · price ${price.toFixed(2)} ÷ {packCoverage > 0 ? packCoverage.toFixed(4) : '—'} {uPacked}
      </span>
      <span title="Product price ÷ pack coverage">
        Coverage cost {uSuffix}{' '}
        <b className="text-emerald-700 tabular-nums" data-testid={`${testid}-coverage-cost`}>
          {fmt(coverageCost)}
        </b>
      </span>
      {labour > 0 && (
        <span>
          Labour {uSuffix}{' '}
          <b className="text-orange-700 tabular-nums">${labour.toFixed(2)}</b>
        </span>
      )}
      {(coverageCost > 0 || labour > 0) && (
        <span className="ml-auto" title={`Coverage cost + labour rate (per ${uPacked})`}>
          Total {uSuffix}{' '}
          <b className="text-slate-900 tabular-nums">{fmt(totalPerUnit)}</b>
        </span>
      )}
    </div>
  );
}


function Cell({ label, span = 2, title, center, children }) {
  // Tailwind needs the explicit `col-span-N` token strings present in
  // the source to JIT-compile them. Map span → class to keep the
  // grid widths configurable without losing safelist coverage.
  const SPAN = {
    1: 'col-span-12 sm:col-span-3 md:col-span-1',
    2: 'col-span-12 sm:col-span-3 md:col-span-2',
    3: 'col-span-12 sm:col-span-4 md:col-span-3',
    4: 'col-span-12 sm:col-span-6 md:col-span-4',
    5: 'col-span-12 sm:col-span-6 md:col-span-5',
    6: 'col-span-12 md:col-span-6',
  };
  return (
    <div className={SPAN[span] || SPAN[2]} title={title}>
      <label className={`block text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-0.5 ${center ? 'text-center' : ''}`}>
        {label}
      </label>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// CopyFromModal — picks another recipe and clones its entries.
// ─────────────────────────────────────────────────────────────────
function CopyFromModal({ api, currentRowId, onClose, onApply }) {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/material-recipes');
        setRecipes((data?.recipes || []).filter((r) => (r.entries || []).length > 0));
      } catch (err) {
        toast.error('Failed to load recipes');
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [api]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return recipes;
    return recipes.filter((r) =>
      `${r.name} ${r.component}`.toLowerCase().includes(q)
      || (r.entries || []).some((e) => `${e.product_name} ${e.supplier}`.toLowerCase().includes(q))
    );
  }, [recipes, query]);

  return (
    <div
      className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="recipe-copy-modal"
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
            <Copy size={14} className="text-violet-600" /> Copy from another recipe
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="recipe-copy-modal-close">
            <X size={16} />
          </button>
        </div>
        <div className="px-4 py-2 border-b border-slate-100">
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter by name / component / product / supplier…"
              data-testid="recipe-copy-modal-search"
              className="w-full pl-7 pr-2 py-1.5 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-violet-400"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {loading ? (
            <div className="py-8 text-center text-slate-400 text-xs">Loading recipes…</div>
          ) : filtered.length === 0 ? (
            <div className="py-8 text-center text-slate-400 text-xs">No recipes match the filter.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {filtered.map((r) => {
                const isCurrent = r.catalogue_row_id === currentRowId;
                return (
                  <li key={r._id} className="py-2 px-2 flex items-center justify-between gap-3 hover:bg-slate-50 rounded">
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-semibold text-slate-800 truncate">
                        {r.name || '(unnamed)'}
                        {isCurrent && <span className="ml-2 text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">current</span>}
                      </div>
                      <div className="text-[10px] text-slate-500 truncate">
                        <b>{r.component || '—'}</b> · {r.entries.length} entr{r.entries.length === 1 ? 'y' : 'ies'} ·{' '}
                        {r.entries.slice(0, 3).map((e) => e.product_name).filter(Boolean).join(' · ')}
                        {r.entries.length > 3 ? ' …' : ''}
                      </div>
                    </div>
                    <button
                      onClick={() => onApply(r.entries || [])}
                      data-testid={`recipe-copy-pick-${r.catalogue_row_id}`}
                      className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded bg-violet-600 text-white hover:bg-violet-700"
                    >
                      Use this
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}



// ─────────────────────────────────────────────────────────────────
// LabourTasksSection — Iter276-labour Phase 2e.
// Renders "Prep / Paint / Pack-up / Travel / Misc" chips to append
// labour tasks to the recipe, plus a table of the current tasks with
// tier picker, hours-per-unit input, and live $-cost readout pulled
// from the active labour cost model.
// ─────────────────────────────────────────────────────────────────
function LabourTasksSection({ tasks, labourModel, region, unitLabels, onAdd, onUpdate, onRemove }) {
  const unitSuffix = unitLabels?.rollupSuffix || '/m²';
  const tiers = labourModel?.staff_tiers || [];
  const tierCharge = (key) => Number((tiers.find((t) => t.key === key) || {}).charge_hourly) || 0;
  const total = (tasks || []).reduce((acc, t) => acc + Number(t.hours_per_unit || 0) * tierCharge(t.tier), 0);
  const noModel = tiers.length === 0;
  return (
    <div className="mt-5 border border-orange-200 rounded-md bg-gradient-to-br from-orange-50 to-white p-3" data-testid="labour-tasks-section">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="text-[10px] uppercase tracking-wider text-orange-700 font-bold flex items-center gap-1.5">
            <Wrench size={12} /> Labour Tasks
          </div>
          {labourModel && (
            <span className="text-[9px] text-slate-500 font-mono uppercase tracking-wider">
              {labourModel.region} · v{labourModel.version}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1">
          <span className="text-[10px] text-slate-500 mr-1">Quick add:</span>
          {LABOUR_TASK_CHIPS.map((chip) => (
            <button
              key={chip.key}
              onClick={() => onAdd(chip)}
              data-testid={`labour-task-add-${chip.key}`}
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded border border-orange-200 bg-white text-orange-700 hover:bg-orange-100"
              title={`Add ${chip.label} labour task (default tier: ${chip.default_tier})`}
            >
              <Plus size={10} /> {chip.label}
            </button>
          ))}
        </div>
      </div>

      {noModel && (
        <div className="mb-2 text-[10px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1">
          ⚠ No active labour cost model for <b>{region}</b> — tier $/hr will show $0 until you configure one in Admin → Labour Cost Model.
        </div>
      )}

      {(tasks || []).length === 0 ? (
        <div className="text-xs text-slate-500 italic py-3 text-center">
          No labour tasks yet. Click a chip above to add one (or use the Misc chip for custom tasks).
        </div>
      ) : (
        <>
          <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold pb-1 border-b border-orange-100">
            <div className="col-span-3">Task</div>
            <div className="col-span-2">Tier</div>
            <div className="col-span-2 text-right">Hours per {unitSuffix.replace('/', '')}</div>
            <div className="col-span-1 text-right">$/hr</div>
            <div className="col-span-2 text-right">Cost {unitSuffix}</div>
            <div className="col-span-1 text-right">Notes</div>
            <div className="col-span-1"></div>
          </div>
          {tasks.map((t, i) => {
            const rate = tierCharge(t.tier);
            const hrs = Number(t.hours_per_unit || 0);
            const cost = hrs * rate;
            return (
              <div key={i} className="grid grid-cols-12 gap-2 items-center py-1.5 border-b border-orange-50 last:border-0" data-testid={`labour-task-row-${i}`}>
                <div className="col-span-3">
                  <input
                    value={t.label ?? ''}
                    onChange={(e) => onUpdate(i, 'label', e.target.value)}
                    placeholder="e.g. Prep, Sanding, Travel to site"
                    data-testid={`labour-task-${i}-label`}
                    className="w-full border border-slate-200 rounded px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-orange-400"
                  />
                </div>
                <div className="col-span-2">
                  <select
                    value={t.tier || 'general'}
                    onChange={(e) => onUpdate(i, 'tier', e.target.value)}
                    data-testid={`labour-task-${i}-tier`}
                    className="w-full border border-slate-200 rounded px-1 py-0.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-orange-400"
                  >
                    {tiers.length === 0 ? (
                      <option value={t.tier || 'general'}>{t.tier || 'general'}</option>
                    ) : tiers.map((tt) => (
                      <option key={tt.key} value={tt.key}>{tt.label || tt.key}</option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2">
                  <input
                    type="number"
                    step="0.01"
                    value={t.hours_per_unit ?? 0}
                    onChange={(e) => onUpdate(i, 'hours_per_unit', Number(e.target.value))}
                    placeholder="0"
                    data-testid={`labour-task-${i}-hours`}
                    className="w-full border border-slate-200 rounded px-1.5 py-0.5 text-xs text-right tabular-nums focus:outline-none focus:ring-1 focus:ring-orange-400"
                  />
                </div>
                <div className="col-span-1 text-right text-xs font-mono text-slate-600 tabular-nums" data-testid={`labour-task-${i}-hourly`}>
                  ${rate.toFixed(2)}
                </div>
                <div className="col-span-2 text-right text-xs font-bold text-orange-700 tabular-nums" data-testid={`labour-task-${i}-cost`}>
                  ${cost.toFixed(2)}
                </div>
                <div className="col-span-1">
                  <input
                    value={t.notes ?? ''}
                    onChange={(e) => onUpdate(i, 'notes', e.target.value)}
                    placeholder="…"
                    data-testid={`labour-task-${i}-notes`}
                    className="w-full border border-slate-200 rounded px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-orange-400"
                  />
                </div>
                <div className="col-span-1 flex justify-end">
                  <button
                    onClick={() => onRemove(i)}
                    className="text-rose-500 hover:text-rose-700"
                    data-testid={`labour-task-${i}-delete`}
                    title="Remove this task"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            );
          })}
          <div className="mt-2 pt-2 border-t border-orange-200 flex items-center justify-end gap-3 text-xs" data-testid="labour-tasks-total">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Labour tasks total:</span>
            <span className="font-bold tabular-nums text-orange-700 text-base">
              ${total.toFixed(2)}
              <span className="text-[10px] font-normal text-slate-500 ml-0.5">{unitSuffix}</span>
            </span>
          </div>
        </>
      )}
    </div>
  );
}
